import React, { useState, useEffect } from 'react';
import { invoiceAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { DollarSign, Plus, FileText, TrendingUp, Receipt, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = { draft: '#5B616E', sent: '#1E5A8A', paid: '#1F7A5A', overdue: '#B3261E', cancelled: '#9A6B1A' };

export default function AdminFinanceFull() {
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [plReport, setPlReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ client_name: '', client_email: '', line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }], tax_rate: 20, currency: 'GBP', notes: '', due_date: '' });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [invRes, expRes, plRes] = await Promise.all([
        invoiceAPI.list().catch(() => ({ data: { invoices: [] } })),
        invoiceAPI.listExpenses().catch(() => ({ data: { expenses: [] } })),
        invoiceAPI.plReport().catch(() => ({ data: null })),
      ]);
      setInvoices(invRes.data.invoices || []);
      setExpenses(expRes.data.expenses || []);
      setPlReport(plRes.data);
    } catch { toast.error('Failed to load finance data'); }
    finally { setLoading(false); }
  };

  const createInvoice = async () => {
    try {
      await invoiceAPI.create(form);
      toast.success('Invoice created');
      setShowCreate(false);
      loadAll();
    } catch { toast.error('Failed to create invoice'); }
  };

  const updateStatus = async (id, status) => {
    try { await invoiceAPI.update(id, { status }); toast.success('Updated'); loadAll(); }
    catch { toast.error('Failed'); }
  };

  const updateLineItem = (i, field, val) => {
    const items = [...form.line_items];
    items[i] = { ...items[i], [field]: val };
    if (field === 'quantity' || field === 'unit_price') {
      items[i].amount = (parseFloat(items[i].quantity) || 0) * (parseFloat(items[i].unit_price) || 0);
    }
    setForm(f => ({ ...f, line_items: items }));
  };

  const addLineItem = () => setForm(f => ({ ...f, line_items: [...f.line_items, { description: '', quantity: 1, unit_price: 0, amount: 0 }] }));

  const totalInvoiced = invoices.filter(i => ['sent','paid'].includes(i.status)).reduce((s, i) => s + (i.total || 0), 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><DollarSign className="w-6 h-6 text-[#B89B5E]" /> Finance</h1>
          <p className="text-sm text-[#5B616E] mt-1">Invoices, expenses, P&L reporting</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> New Invoice</Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Invoiced', value: `£${totalInvoiced.toFixed(2)}`, icon: FileText, color: '#1E5A8A' },
          { label: 'Total Collected', value: `£${totalPaid.toFixed(2)}`, icon: DollarSign, color: '#1F7A5A' },
          { label: 'Gross Revenue', value: plReport ? `£${plReport.revenue?.total.toFixed(2)}` : '—', icon: TrendingUp, color: '#B89B5E' },
          { label: 'Gross Profit', value: plReport ? `£${plReport.gross_profit.toFixed(2)}` : '—', icon: TrendingUp, color: plReport?.gross_profit >= 0 ? '#1F7A5A' : '#B3261E' },
        ].map(k => (
          <Card key={k.label} className="bg-white border-[#E7E0D6]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1"><k.icon className="w-4 h-4" style={{ color: k.color }} /><span className="text-xs text-[#5B616E]">{k.label}</span></div>
              <p className="text-2xl font-bold" style={{ color: k.color }}>{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="invoices">
        <TabsList><TabsTrigger value="invoices">Invoices</TabsTrigger><TabsTrigger value="expenses">Expenses</TabsTrigger><TabsTrigger value="pl">P&L Report</TabsTrigger></TabsList>

        <TabsContent value="invoices" className="mt-4">
          <Card className="bg-white border-[#E7E0D6]">
            <CardContent className="pt-4">
              {loading ? <Skeleton className="h-40" /> : invoices.length === 0 ? (
                <p className="text-sm text-[#5B616E] text-center py-8">No invoices yet. Create your first invoice.</p>
              ) : (
                <div className="space-y-2">
                  {invoices.map(inv => (
                    <div key={inv.id} className="flex items-center justify-between p-3 border border-[#E7E0D6] rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{inv.invoice_number} — {inv.client_name}</p>
                        <p className="text-xs text-[#5B616E]">{inv.currency} {inv.total?.toFixed(2)} · Due {inv.due_date}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge style={{ background: `${STATUS_COLORS[inv.status] || '#5B616E'}20`, color: STATUS_COLORS[inv.status] || '#5B616E' }} className="text-[10px]">{inv.status}</Badge>
                        <a href={invoiceAPI.htmlUrl(inv.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="h-7 p-1"><ExternalLink className="w-3.5 h-3.5" /></Button></a>
                        {inv.status === 'draft' && <Button size="sm" variant="outline" className="text-xs" onClick={() => updateStatus(inv.id, 'sent')}>Send</Button>}
                        {inv.status === 'sent' && <Button size="sm" variant="outline" className="text-xs text-green-700 border-green-200" onClick={() => updateStatus(inv.id, 'paid')}>Mark Paid</Button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expenses" className="mt-4">
          <Card className="bg-white border-[#E7E0D6]">
            <CardContent className="pt-4">
              {expenses.length === 0 ? <p className="text-sm text-[#5B616E] text-center py-8">No expenses recorded.</p> : (
                <div className="space-y-2">
                  {expenses.map(e => (
                    <div key={e.id} className="flex items-center justify-between p-3 border border-[#E7E0D6] rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{e.description}</p>
                        <p className="text-xs text-[#5B616E]">{e.currency} {e.amount?.toFixed(2)} · {e.category} · {e.date}</p>
                      </div>
                      <Badge style={{ background: e.status === 'approved' ? '#1F7A5A20' : '#9A6B1A20', color: e.status === 'approved' ? '#1F7A5A' : '#9A6B1A' }} className="text-[10px]">{e.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pl" className="mt-4">
          {plReport && (
            <Card className="bg-white border-[#E7E0D6]">
              <CardHeader><CardTitle className="text-sm">P&L Report — {plReport.year}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-semibold text-[#5B616E] uppercase mb-2">Revenue</p>
                    {Object.entries(plReport.revenue || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-sm py-1 border-b border-[#E7E0D6]">
                        <span className="capitalize">{k.replace('_', ' ')}</span>
                        <span className="font-medium">{plReport.currency} {v}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#5B616E] uppercase mb-2">Expenses</p>
                    <div className="flex justify-between text-sm py-1 border-b border-[#E7E0D6]"><span>Total</span><span className="font-medium">{plReport.currency} {plReport.expenses?.total}</span></div>
                    <div className="flex justify-between text-sm py-1 border-b border-[#E7E0D6] font-bold mt-2"><span>Gross Profit</span><span style={{ color: plReport.gross_profit >= 0 ? '#1F7A5A' : '#B3261E' }}>{plReport.currency} {plReport.gross_profit}</span></div>
                    <div className="flex justify-between text-sm py-1"><span>Margin</span><span className="font-medium">{plReport.margin_pct}%</span></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Invoice */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-medium mb-1 block">Client Name</label><Input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} /></div>
              <div><label className="text-xs font-medium mb-1 block">Client Email</label><Input type="email" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} /></div>
              <div><label className="text-xs font-medium mb-1 block">Tax Rate (%)</label><Input type="number" value={form.tax_rate} onChange={e => setForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) || 0 }))} /></div>
              <div><label className="text-xs font-medium mb-1 block">Due Date</label><Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} /></div>
            </div>
            <div>
              <label className="text-xs font-medium mb-2 block">Line Items</label>
              {form.line_items.map((item, i) => (
                <div key={i} className="grid grid-cols-4 gap-2 mb-2">
                  <Input className="col-span-2 text-xs" placeholder="Description" value={item.description} onChange={e => updateLineItem(i, 'description', e.target.value)} />
                  <Input className="text-xs" type="number" placeholder="Qty" value={item.quantity} onChange={e => updateLineItem(i, 'quantity', e.target.value)} />
                  <Input className="text-xs" type="number" placeholder="Unit price" value={item.unit_price} onChange={e => updateLineItem(i, 'unit_price', e.target.value)} />
                </div>
              ))}
              <Button size="sm" variant="outline" className="text-xs" onClick={addLineItem}><Plus className="w-3 h-3 mr-1" /> Add line</Button>
            </div>
            <Input placeholder="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createInvoice} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Create Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
